(() => {
  const COORDS = {
    US: [39.5, -98.35], CA: [56.1, -106.35], MX: [23.63, -102.55], BR: [-14.24, -51.93],
    UK: [54.0, -2.0], GB: [54.0, -2.0], IE: [53.41, -8.24], DE: [51.17, 10.45], FR: [46.6, 2.2],
    IT: [42.8, 12.5], ES: [40.4, -3.7], PT: [39.4, -8.22], NL: [52.13, 5.29], BE: [50.5, 4.47],
    SE: [62.0, 15.0], NO: [60.47, 8.47], DK: [56.26, 9.5], FI: [61.92, 25.75], PL: [52.0, 19.4],
    AT: [47.52, 14.55], CH: [46.82, 8.23], CZ: [49.82, 15.47], TR: [38.96, 35.24],
    AE: [23.42, 53.85], SA: [23.89, 45.08], IN: [22.0, 79.0], SG: [1.35, 103.82], MY: [4.21, 101.98],
    TH: [15.87, 100.99], VN: [14.06, 108.28], PH: [12.88, 121.77], ID: [-0.79, 113.92],
    CN: [35.86, 104.2], HK: [22.32, 114.17], TW: [23.7, 120.96], KR: [36.5, 127.8], JP: [36.2, 138.25],
    AU: [-25.27, 133.78], NZ: [-40.9, 174.89], ZA: [-30.56, 22.94]
  };
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const SPEED = 0.0025;
  let loader = null;

  function loadCobe(src) {
    if (window.LsCobe) return Promise.resolve(window.LsCobe);
    loader = loader || new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = () => (window.LsCobe ? resolve(window.LsCobe) : reject(new Error('cobe')));
      s.onerror = reject;
      document.head.append(s);
    });
    return loader;
  }

  function parseLoc(value) {
    const parts = String(value || '').split(',').map((v) => parseFloat(v));
    return parts.length === 2 && parts.every(Number.isFinite) ? parts : null;
  }

  function readMarkers(root) {
    let data = [];
    try {
      data = JSON.parse(root.querySelector('[data-lhf-globe-markers]').textContent);
    } catch (e) {}
    const seen = new Set();
    const out = [];
    data.forEach((d) => {
      const code = String(d.code || '').trim().toUpperCase();
      const loc = parseLoc(d.loc) || COORDS[code];
      if (!loc) return;
      const id = 'lhf-' + (code || loc.join('_')).toLowerCase().replace(/[^a-z0-9]/g, '-');
      if (seen.has(id)) return;
      seen.add(id);
      out.push({ id, location: loc, size: 0.028 });
    });
    return out;
  }

  function focusPhi(lon) {
    return Math.PI - ((lon * Math.PI) / 180 - Math.PI / 2);
  }

  function mount(root) {
    if (root.dataset.lhfGlobeReady) return;
    root.dataset.lhfGlobeReady = '1';
    const canvas = root.querySelector('canvas');
    const markers = readMarkers(root);
    const cs = getComputedStyle(root);
    const rgb = (name, fallback) => {
      const v = cs.getPropertyValue(name).trim().split(/[\s,]+/).map(Number);
      return v.length === 3 && v.every(Number.isFinite) ? v : fallback;
    };

    let globe = null;
    let raf = 0;
    let phi = focusPhi(10);
    let offset = 0;
    let drag = null;
    let dragDelta = 0;
    let inView = false;
    let width = 0;

    markers.forEach((m, i) => {
      const pulse = document.createElement('span');
      pulse.className = 'lhf-globe-pulse';
      pulse.style.setProperty('position-anchor', '--cobe-' + m.id);
      pulse.style.setProperty('--lhf-pulse-delay', ((i * 0.37) % 2).toFixed(2) + 's');
      pulse.style.opacity = 'var(--cobe-visible-' + m.id + ', 0)';
      root.append(pulse);
    });

    function frame() {
      if (!root.isConnected) {
        globe.destroy();
        raf = 0;
        return;
      }
      if (!drag && !reduced.matches) phi += SPEED;
      globe.update({ phi: phi + offset + dragDelta });
      raf = inView ? requestAnimationFrame(frame) : 0;
    }

    function play() {
      if (globe && !raf && inView) raf = requestAnimationFrame(frame);
    }

    function create(createGlobe) {
      width = canvas.offsetWidth;
      if (!width || globe) return;
      globe = createGlobe(canvas, {
        devicePixelRatio: Math.min(window.devicePixelRatio || 1, 2),
        width,
        height: width,
        phi,
        theta: 0.28,
        dark: 1,
        diffuse: 1.4,
        mapSamples: 16000,
        mapBrightness: 7,
        baseColor: rgb('--lhf-globe-base', [0.32, 0.32, 0.34]),
        markerColor: rgb('--lhf-globe-marker', [0.96, 0.96, 0.96]),
        glowColor: rgb('--lhf-globe-glow', [0.09, 0.09, 0.1]),
        markerElevation: 0,
        markers,
        opacity: 0.85
      });
      globe.update({ phi });
      root.classList.add('is-ready');
      play();
    }

    canvas.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      drag = { x: e.clientX };
      root.classList.add('is-dragging');
      canvas.setPointerCapture?.(e.pointerId);
    });
    canvas.addEventListener('pointermove', (e) => {
      if (drag) dragDelta = (e.clientX - drag.x) / 200;
      if (drag && !raf) globe?.update({ phi: phi + offset + dragDelta });
    });
    const release = () => {
      if (!drag) return;
      offset += dragDelta;
      dragDelta = 0;
      drag = null;
      root.classList.remove('is-dragging');
    };
    canvas.addEventListener('pointerup', release);
    canvas.addEventListener('pointercancel', release);
    canvas.addEventListener('lostpointercapture', release);

    new ResizeObserver(() => {
      const w = canvas.offsetWidth;
      if (!globe) {
        if (w && window.LsCobe) create(window.LsCobe);
        return;
      }
      if (w && w !== width) {
        width = w;
        globe.update({ width: w, height: w });
      }
    }).observe(root);

    new IntersectionObserver((entries) => {
      inView = entries[0].isIntersecting;
      if (inView) play();
    }).observe(root);

    loadCobe(root.dataset.cobeSrc).then(create).catch(() => root.classList.add('is-failed'));
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-lhf-globe]').forEach((root) => {
      if (root.dataset.lhfGlobeWatch) return;
      root.dataset.lhfGlobeWatch = '1';
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        mount(root);
      }, { rootMargin: '600px 0px' });
      io.observe(root);
    });
  }

  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
